import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import Head from "../Base/Head";
import { formatCurrency } from "../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCrown } from "@fortawesome/free-solid-svg-icons";

const GuessContainer = ({
  title,
  hostedBy,
  contestLink,
  info,
  winner,
  isHigh,
  delay,
  archived,
  finished,
}) => {
  const [showGuesses, setShowGuesses] = useState(false);

  const year = title.split(" ").pop();

  const fadeInOutVariant = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.5 } },
    exit: { opacity: 0, transition: { duration: 0.2 } },
  };

  return (
    <motion.div
      className={`guess-container ${isHigh ? "high" : ""} ${
        archived ? "archived" : ""
      }`}
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0, transition: { duration: 0.8, delay } }}
    >
      <motion.img
        src={process.env.PUBLIC_URL + "/images/btc-logo-shadow.png"}
        alt="Decorative"
        className="decorative-image low"
        initial={{ opacity: 0, scale: 0, rotate: -180 }}
        animate={{
          opacity: 1,
          scale: 1,
          rotate: 0,
          transition: { duration: 1 },
        }}
      />
      <div className="title">
        <a
          target="_blank"
          href={contestLink}
          className={isHigh ? "high" : "low"}
        >
          {title}
        </a>
      </div>
      <div className="hosted">
        Hosted by <a href={contestLink}>{hostedBy}</a>
      </div>
      <div className="info-container">
        <div className="info-block">
          <div className="info-title">
            {isHigh
              ? `Highest Price ${archived ? `in ${year}` : `This Year`}`
              : `Lowest Price ${archived ? `in ${year}` : `This Year`}`}
          </div>
          <div className="info-sub">(Coinbase - USD)</div>
          <div className={`info-price ${isHigh ? "highest" : ""}`}>
            ${formatCurrency(isHigh ? info.highestPrice : info.lowestPrice)}
          </div>
        </div>
        <div className="info-block">
          <div className="info-title">
            {finished ? "Winner" : "Current Winner"}
            {finished && (
              <FontAwesomeIcon icon={faCrown} className="winner-crown" />
            )}
          </div>
          <div className="info-winner">
            <a
              target="_blank"
              href={`https://reddit.com/u/${winner.username}`}
              className={`user ${isHigh ? "" : "lowest"}`}
            >
              {winner.username}
            </a>
            <span className="separator">-</span>${formatCurrency(winner.guess)}
          </div>
        </div>
      </div>
      <div
        className="toggle-guesses"
        onClick={() => setShowGuesses(!showGuesses)}
      >
        {!showGuesses ? `View Guesses` : `Hide Guesses`}
      </div>
      <AnimatePresence>
        {showGuesses && (
          <motion.div
            className="guesses-container"
            variants={fadeInOutVariant}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="totals">
              <b>{info.guesses.length} Guesses</b>{" "}
              <span className="mob-only">
                <br />
              </span>
              <div className="totals-breakdown">
                {finished ? (
                  isHigh ? (
                    <>
                      ({info.guesses.filter((g) => g.eliminated).length} Too Low
                      /{" "}
                      {info.guesses.filter((g) => g.finishedEliminated).length}{" "}
                      Too High)
                    </>
                  ) : (
                    <>
                      ({info.guesses.filter((g) => g.finishedEliminated).length}{" "}
                      Too Low /{" "}
                      {info.guesses.filter((g) => g.eliminated).length} Too
                      High)
                    </>
                  )
                ) : (
                  <>
                    {" "}
                    ({info.guesses.filter((g) => !g.eliminated).length} Active /{" "}
                    {info.guesses.filter((g) => g.eliminated).length}{" "}
                    Eliminated)
                  </>
                )}
              </div>
            </div>
            {info.guesses.map((g, i) => {
              const guessClass = g.winning
                ? "winning"
                : g.eliminated
                ? "eliminated"
                : g.finishedEliminated
                ? "finished-eliminated"
                : "";
              return (
                <div key={i} className={`guess ${guessClass}`}>
                  <a
                    target="_blank"
                    href={`https://reddit.com/u/${g.username}`}
                    className="user"
                  >
                    {g.username}
                  </a>
                  <span className="separator">-</span>${formatCurrency(g.guess)}
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default function GuessTheBTC({ info }) {
  return (
    <>
      <Head
        head={{
          title: "Guess the BTC Price - r/BitcoinMarkets",
          description: "Guess the BTC Price contests from /r/BitcoinMarkets",
          url: "https://GuessTheBTC.com",
          image: "https://i.ibb.co/fDTKgn3/twitter-card.jpg",
        }}
      />
      <div className="emu-wrapper">
        <motion.div
          className="reddit-title"
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0, transition: { duration: 0.8 } }}
        >
          <a href="https://reddit.com/r/BitcoinMarkets" target="_blank">
            /r/BitcoinMarkets
          </a>
        </motion.div>

        {/* Current Contests */}
        <GuessContainer
          title="Guess the Low 2026"
          hostedBy="/u/imissusenet"
          contestLink="https://www.reddit.com/r/BitcoinMarkets/comments/1pmhrzu/5th_annual_guess_the_low_contest/"
          info={info["2026"].lowInfo}
          winner={info["2026"].lowInfo.guesses.find((g) => g.winning)}
          isHigh={false}
          delay={0}
          finished={info["2026"].finished}
        />
        <div className="contest-separator"></div>
        <GuessContainer
          title="Guess the High 2026"
          hostedBy="/u/YouNeedAVacation"
          contestLink="https://www.reddit.com/r/BitcoinMarkets/comments/1pn3f4e/2026_guess_the_high_contest/"
          info={info["2026"].highInfo}
          winner={info["2026"].highInfo.guesses.find((g) => g.winning)}
          isHigh={true}
          delay={0.3}
          finished={info["2026"].finished}
        />

        <div className="contest-separator"></div>
        <div className="previous-contests">Previous Contests</div>

        {/* Previous: 2025 */}
        <GuessContainer
          title="Guess the Low 2025"
          hostedBy="/u/imissusenet"
          contestLink="https://www.reddit.com/r/BitcoinMarkets/comments/1h47grk/2025_guess_the_low_contest/"
          info={info["2025"].lowInfo}
          winner={info["2025"].lowInfo.guesses.find((g) => g.winning)}
          isHigh={false}
          delay={0.6}
          archived={true}
          finished={info["2025"].finished}
        />
        <div className="contest-separator"></div>
        <GuessContainer
          title="Guess the High 2025"
          hostedBy="/u/YouNeedAVacation"
          contestLink="https://www.reddit.com/r/BitcoinMarkets/comments/1hr2ybo/2025_guess_the_high_contest/"
          info={info["2025"].highInfo}
          winner={info["2025"].highInfo.guesses.find((g) => g.winning)}
          isHigh={true}
          delay={0.9}
          archived={true}
          finished={info["2025"].finished}
        />

        {/* Previous: 2024 */}
        <div className="contest-separator"></div>
        <GuessContainer
          title="Guess the Low 2024"
          hostedBy="/u/imissusenet"
          contestLink="https://www.reddit.com/r/BitcoinMarkets/comments/189ulaa/3rd_annual_guess_the_low_contest/"
          info={info["2024"].lowInfo}
          winner={info["2024"].lowInfo.guesses.find((g) => g.winning)}
          isHigh={false}
          delay={1.2}
          archived={true}
          finished={info["2024"].finished}
        />
        <div className="contest-separator"></div>
        <GuessContainer
          title="Guess the High 2024"
          hostedBy="/u/4theWlN"
          contestLink="https://www.reddit.com/r/BitcoinMarkets/comments/18racz2/1st_annual_guess_the_high_contest/"
          info={info["2024"].highInfo}
          winner={info["2024"].highInfo.guesses.find((g) => g.winning)}
          isHigh={true}
          delay={1.5}
          archived={true}
          finished={info["2024"].finished}
        />

        <motion.footer
          className="footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.5 } }}
        >
          Website created and maintained by{" "}
          <a
            href="https://reddit.com/u/YouNeedAVacation"
            target="_blank"
            rel="noopener noreferrer"
          >
            /u/YouNeedAVacation
          </a>
        </motion.footer>
      </div>
    </>
  );
}